import useStore from "../store.ts";
import updateCoordinator from "../utils/UpdateCoordinator.ts";
import {resetSlowPathBuffer} from "../utils/DatasheetUtils.ts";
import {getRenderWorkSeq, postRenderRequest} from "../worker/RenderWorkerManager.ts";
import {type UpdateContextHandle, updateContextRegistry, UpdateOrigin,} from "../utils/UpdateContextRegistry.ts";

type QueuedUpdate = {
    origin: UpdateOrigin;
    execute: (handle: UpdateContextHandle) => void | Promise<void>;
    // Called if the op is dropped without executing (queue clear). Lets callers
    // awaiting the op (fireInitial, reconcile, datasheet builds) fail fast
    // instead of hanging on a promise that never settles.
    onDrop?: (err: Error) => void;
    // Channel name for row ops, so a channel.reset can drop the pre-reset backlog (#c804).
    channel?: string;
};

// see docs/EXTRACTED_COMMENTS.md#c136
const ITERATION_WATCHDOG_MS: Partial<Record<UpdateOrigin, number>> = {
    [UpdateOrigin.DatasheetBuild]: 15_000,
    [UpdateOrigin.Initial]:        30_000,
    [UpdateOrigin.Reconnect]:      30_000,
    [UpdateOrigin.LayoutRebuild]:  30_000,
    // Per-row now that the iteration awaits the body: a postUpdate that never
    // acks must not hold the refill for the 60s default.
    [UpdateOrigin.ChannelReset]:    5_000,
};
const ITERATION_WATCHDOG_DEFAULT_MS = 60_000;


/* see docs/EXTRACTED_COMMENTS.md#c139 */
export class UpdateQueueManager {
    // see docs/EXTRACTED_COMMENTS.md#c744
    private static readonly OVERFLOW_HARD_CAP = 10_000;
    // Safety valve on the render gate: a render reply that never lands must not hold the
    // queue forever. The watchdog is already cleared by the time the gate opens. Sized to
    // never fire in normal operation — a legitimate round-trip can far exceed a single
    // RENDER_COALESCE_MS window (scheduleRender re-arms while geometry keeps arriving, and
    // a deferred batch additionally waits for the pipeline tick to see no active contexts).
    // Firing early silently drops the ordering guarantee this gate exists for, so it is a
    // hang-breaker, not a latency control. Sits between ChannelReset's 5s per-row watchdog
    // and DatasheetBuild's 15s.
    private static readonly RENDER_GATE_TIMEOUT_MS = 10_000;

    private updateQueue: QueuedUpdate[] = [];
    private isExecutingUpdate = false;

    /* see docs/EXTRACTED_COMMENTS.md#c140 */
    private _sessionId = 0;
    private _activeIterationSession: number | null = null;

    // Lightweight telemetry for drain-iteration behavior.
    private iterationsExecuted = 0;
    private totalOpsExecuted = 0;
    private maxOpsPerIterationSeen = 0;

    // see docs/EXTRACTED_COMMENTS.md#c723
    private renderSeqAtIterationStart = 0;

    // see docs/EXTRACTED_COMMENTS.md#c141
    private activeWatchdog: ReturnType<typeof setTimeout> | null = null;
    private activeWatchdogContextId: string | null = null;
    private activeWatchdogOrigin: UpdateOrigin | null = null;
    private timedOutThisIteration: boolean = false;

    private getLogicalQueueLength(): number {
        return this.updateQueue.length;
    }

    public getSessionId(): number {
        return this._sessionId;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c142 */
    public isActiveSession(sessionId: number): boolean {
        return sessionId === this._sessionId && this._activeIterationSession === sessionId;
    }

    /** Advance the session counter; the new value is returned. */
    public bumpSession(): number {
        this._sessionId += 1;
        this._activeIterationSession = null;
        return this._sessionId;
    }

    public queueUpdate(
        origin: UpdateOrigin,
        execute: (handle: UpdateContextHandle) => void,
        onDrop?: (err: Error) => void,
        channel?: string,
    ): void {
        console.log(`[UpdateQueue] queued origin=${origin}`);
        this.updateQueue.push({ origin, execute, onDrop, channel });

        // see docs/EXTRACTED_COMMENTS.md#c744
        const depth = this.getLogicalQueueLength();
        if (depth > UpdateQueueManager.OVERFLOW_HARD_CAP) {
            console.warn(`[UpdateQueue] backlog depth=${depth} exceeds hard cap — resyncing (full repaint)`);
            this.clear();
            useStore().socketManager.recycle("update_queue_overflow");
            return;
        }

        // see docs/EXTRACTED_COMMENTS.md#c534
        const isLive = useStore().socketManager.supervisor?.isLive();
        if (!isLive) {
            return;
        }

        if (!this.isExecutingUpdate) {
            this.processNextIteration();
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c804 */
    public dropChannelOps(channel: string, origins: ReadonlySet<UpdateOrigin>): number {
        const kept: QueuedUpdate[] = [];
        const dropped: QueuedUpdate[] = [];
        for (const op of this.updateQueue) {
            (op.channel === channel && origins.has(op.origin) ? dropped : kept).push(op);
        }
        if (dropped.length === 0) return 0;
        this.updateQueue = kept;
        const err = new Error(`UpdateQueueManager: queued update dropped by channel.reset (${channel})`);
        for (const op of dropped) {
            try {
                op.onDrop?.(err);
            } catch (_e) {
                // onDrop is a rejection sink; never let it break the drop.
            }
        }
        return dropped.length;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c535 */
    public notifyLive(): void {
        if (this.isExecutingUpdate) return;
        if (this.updateQueue.length === 0) return;
        this.processNextIteration();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c536 */
    private onIterationComplete(session: number): void {
        if (session !== this._sessionId) {
            return;
        }
        // see docs/EXTRACTED_COMMENTS.md#c537
        const finishedOrigin = this.activeWatchdogOrigin;
        console.log(`[UpdateQueue] finished origin=${finishedOrigin !== null ? (finishedOrigin) : "?"} status=${this.timedOutThisIteration ? "timed_out" : "completed"}`);
        this.clearWatchdog();
        // see docs/EXTRACTED_COMMENTS.md#c723 — no render work was requested
        // during this iteration, so the gate has nothing to order. Any origin:
        // gated pre-load data ops must drain at memory speed, not gate pace.
        const skipGate = getRenderWorkSeq() === this.renderSeqAtIterationStart;
        // see docs/EXTRACTED_COMMENTS.md#c697
        void this.resumeAfterRender(session, finishedOrigin, skipGate);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c697 */
    private async resumeAfterRender(session: number, origin: UpdateOrigin | null, skipGate: boolean = false): Promise<void> {
        if (skipGate) {
            // One microtask hop — skipped iterations must not recurse the stack.
            await Promise.resolve();
        } else {
            let timer: ReturnType<typeof setTimeout> | null = null;
            try {
                const outcome = await Promise.race([
                    postRenderRequest(["updateFlush"]).then(() => "applied" as const),
                    new Promise<"timed_out">((resolve) => {
                        timer = setTimeout(() => resolve("timed_out"),
                            UpdateQueueManager.RENDER_GATE_TIMEOUT_MS);
                    }),
                ]);
                // Not cosmetic: resuming early means this iteration's render never completed,
                // so the ordering the gate provides is gone for it. Silence here would read as
                // "the gate held" — the exact wrong conclusion when timings need tuning.
                if (outcome === "timed_out") {
                    console.warn(
                        `[UpdateQueue] render gate timed out after ` +
                        `${UpdateQueueManager.RENDER_GATE_TIMEOUT_MS}ms (origin=${origin ?? "?"}) — ` +
                        `resuming without a completed render pass`
                    );
                }
            } catch (_e) {
                // A render failure must never wedge the queue.
            } finally {
                if (timer != null) clearTimeout(timer);
            }
        }
        // clear() bumped the session while we waited — that path already reset
        // isExecutingUpdate and re-kicked the drain, so leave its state alone.
        if (session !== this._sessionId) return;
        this.isExecutingUpdate = false;
        if (this.updateQueue.length > 0) {
            this.processNextIteration();
        }
    }

    private processNextIteration(): void {
        if (this.isExecutingUpdate) return;
        if (this.updateQueue.length === 0) return;

        this.isExecutingUpdate = true;
        const session = this._sessionId;
        this._activeIterationSession = session;

        // The drain is strictly one op per iteration — no origin coalescing.
        const op = this.updateQueue.shift()!;

        this.iterationsExecuted++;
        this.totalOpsExecuted += 1;
        this.maxOpsPerIterationSeen = Math.max(this.maxOpsPerIterationSeen, 1);

        const origin = op.origin;
        const handle = updateContextRegistry.create(origin, () => this.onIterationComplete(session));
        handle.sessionId = session;

        this.renderSeqAtIterationStart = getRenderWorkSeq();
        this.timedOutThisIteration = false;
        // see docs/EXTRACTED_COMMENTS.md#c539
        this.armWatchdog(handle.id, origin, session);
        void (async () => {
            try {
                await op.execute(handle);
            } catch (err) {
            } finally {
                if (session === this._sessionId) {
                    updateContextRegistry.seal(handle);
                } else {
                    // see docs/EXTRACTED_COMMENTS.md#c540
                    this.clearWatchdog();
                }
            }
        })();
    }

    private armWatchdog(contextId: string, origin: UpdateOrigin, session: number): void {
        this.clearWatchdog();
        const ms = ITERATION_WATCHDOG_MS[origin] ?? ITERATION_WATCHDOG_DEFAULT_MS;
        this.activeWatchdogContextId = contextId;
        this.activeWatchdogOrigin = origin;
        this.activeWatchdog = setTimeout(() => {
            // Stale fire: clear() or onIterationComplete already ran.
            if (session !== this._sessionId) {
                return;
            }
            if (this.activeWatchdogContextId !== contextId) {
                return;
            }
            console.warn(`[UpdateQueue] iteration watchdog fired after ${ms}ms (origin=${origin}) — force-completing context`);
            this.timedOutThisIteration = true;
            // see docs/EXTRACTED_COMMENTS.md#c542
            updateCoordinator.forceCompleteContext(contextId);
        }, ms);
    }

    private clearWatchdog(): void {
        if (this.activeWatchdog !== null) {
            clearTimeout(this.activeWatchdog);
            this.activeWatchdog = null;
        }
        this.activeWatchdogContextId = null;
        this.activeWatchdogOrigin = null;
    }

    public clear(): void {
        this.bumpSession();
        const dropped = this.updateQueue.splice(0, this.updateQueue.length);
        // Settle the dropped ops — a spliced-out op whose promise never resolves
        // NOR rejects leaves its awaiter (fireInitial, reconcile, datasheet
        // builds) hung forever with store.loaded never set.
        if (dropped.length > 0) {
            const err = new Error("UpdateQueueManager cleared: queued update dropped");
            for (const op of dropped) {
                try {
                    op.onDrop?.(err);
                } catch (_e) {
                    // onDrop is a rejection sink; never let it break the clear.
                }
            }
        }
        this.clearWatchdog();
        this.timedOutThisIteration = false;
        this.isExecutingUpdate = false;
        this.iterationsExecuted = 0;
        this.totalOpsExecuted = 0;
        this.maxOpsPerIterationSeen = 0;
        updateContextRegistry.clear();
        // see docs/EXTRACTED_COMMENTS.md#c544
        resetSlowPathBuffer();
    }

    public get queueLength(): number {
        return this.getLogicalQueueLength();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c545 */
    public getDebugSnapshot(): {
        queueLength: number;
        isExecutingUpdate: boolean;
        iterationsExecuted: number;
        totalOpsExecuted: number;
        maxOpsPerIterationSeen: number;
        pendingOrigins: UpdateOrigin[];
    } {
        return {
            queueLength: this.updateQueue.length,
            isExecutingUpdate: this.isExecutingUpdate,
            iterationsExecuted: this.iterationsExecuted,
            totalOpsExecuted: this.totalOpsExecuted,
            maxOpsPerIterationSeen: this.maxOpsPerIterationSeen,
            pendingOrigins: this.updateQueue.map((u) => u.origin),
        };
    }
}
